import AnimatedContainer from "../../container/animatedContainer";
import type { ReactNode } from "react";
import { BsTicketPerforated } from "react-icons/bs";
import { CgChevronLeftO, CgChevronRightO } from "react-icons/cg";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { GoArrowRight } from "react-icons/go";
import { HiOutlineExclamationCircle } from "react-icons/hi2";
import {
  FiCalendar,
  FiClock,
  FiGift,
  FiMapPin,
  FiMic,
  FiRefreshCw,
} from "react-icons/fi";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { useGetSingleEventQuery } from "../../api/event";
import { Ticket } from "../../types/event";
import { usePaymentMutation } from "../../api/tickets";
import { useForm } from "react-hook-form";
import mtn_logo from "../../assets/images/mtn-logo.png";
import { useAppSelector } from "../../store";
import { PaymentPayload } from "../../types/tickets";
import { useVerifyPromoCodeMutation } from "../../api/promo_code";
import {
  campaign,
  displayTicketType,
  formatCampaignCurrency,
  isCampaignEvent,
  normalizeTicketType,
  sortTickets,
} from "../../config/campaign";

type SelectedTicket = {
  pricing: Ticket;
  ticket_number: number;
  amount?: number;
};

const minimumSupportAmount = 20000;
const supportAmountOptions = [20000, 30000, 50000, 70000, 100000];

const countryCodes = [
  { code: "250", flag: "🇷🇼", name: "Rwanda", localExample: "7XXXXXXXX" },
  { code: "254", flag: "🇰🇪", name: "Kenya", localExample: "7XXXXXXXX" },
  { code: "256", flag: "🇺🇬", name: "Uganda", localExample: "7XXXXXXXX" },
  { code: "255", flag: "🇹🇿", name: "Tanzania", localExample: "7XXXXXXXX" },
  { code: "257", flag: "🇧🇮", name: "Burundi", localExample: "7XXXXXXX" },
  { code: "243", flag: "🇨🇩", name: "DR Congo", localExample: "8XXXXXXXX" },
];

const defaultCountryCode = "250";

export default function EventDetails() {
  const [cerror, setCerror] = useState<string | null>(null);
  const { slug } = useParams();
  const isCampaignSlug = Boolean(
    slug && (slug === campaign.fallbackSlug || isCampaignEvent(undefined, slug)),
  );
  const { data, isLoading } = useGetSingleEventQuery(
    { slug: campaign.fallbackSlug },
    { skip: !isCampaignSlug },
  );
  const [steps, setSteps] = useState<1 | 2 | 3>(1);
  const [selected, setSelected] = useState<SelectedTicket[]>([]);
  const [customAmounts, setCustomAmounts] = useState<Record<string, number>>(
    {},
  );
  const [amountInputs, setAmountInputs] = useState<Record<string, string>>(
    {},
  );
  const [paymentMode] = useState<"momo" | "code">("momo");
  const [payment, payOptions] = usePaymentMutation();
  const [verify, promoOptions] = useVerifyPromoCodeMutation();
  const { user } = useAppSelector((state) => state.userState);

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    watch,
    setError,
  } = useForm({
    defaultValues: {
      email: user?.email ?? "",
      name: user?.name ?? "",
      phoneNumber: "",
      phoneCountryCode: defaultCountryCode,
      payment_mode: paymentMode,
      promoCode: null,
      personalPhoneNumber: getLocalPhoneNumber(user?.phoneNumber),
      personalPhoneCountryCode: defaultCountryCode,
    },
  });

  const getTicketNumber = (id: string): number => {
    const selectedTicket = selected.find((ticket) => ticket.pricing.id === id);
    return selectedTicket ? selectedTicket.ticket_number : 0;
  };

  const getMinimumAmount = (pricing: Ticket) =>
    isVariablePricing(pricing)
      ? Math.max(pricing.price, minimumSupportAmount)
      : pricing.price;

  const getCustomAmount = (pricing: Ticket) =>
    customAmounts[pricing.id] ?? getMinimumAmount(pricing);

  const getAmountInputValue = (pricing: Ticket) =>
    amountInputs[pricing.id] ?? String(getCustomAmount(pricing));

  const isCustomAmountValid = (pricing: Ticket) => {
    const raw = amountInputs[pricing.id];

    if (raw !== undefined) {
      if (raw.trim() === "") return false;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed >= getMinimumAmount(pricing);
    }

    return getCustomAmount(pricing) >= getMinimumAmount(pricing);
  };

  const getSelectedAmount = (ticket: SelectedTicket) =>
    isVariablePricing(ticket.pricing)
      ? (ticket.amount ?? getMinimumAmount(ticket.pricing))
      : ticket.pricing.price;

  const upsertSelectedAmount = (pricing: Ticket, amount: number) => {
    setSelected((prev) => {
      const existingTicket = prev.find(
        (ticket) => ticket.pricing.id === pricing.id,
      );

      if (existingTicket) {
        return prev.map((ticket) =>
          ticket.pricing.id === pricing.id ? { ...ticket, amount } : ticket,
        );
      }

      return [...prev, { pricing, ticket_number: 1, amount }];
    });
  };

  const updateCustomAmount = (pricing: Ticket, amount: number) => {
    const normalizedAmount = Number.isFinite(amount) ? amount : 0;

    setAmountInputs((prev) => {
      const next = { ...prev };
      delete next[pricing.id];
      return next;
    });

    setCustomAmounts((prev) => ({
      ...prev,
      [pricing.id]: normalizedAmount,
    }));

    upsertSelectedAmount(pricing, normalizedAmount);
  };

  const handleAmountInputChange = (pricing: Ticket, raw: string) => {
    setAmountInputs((prev) => ({ ...prev, [pricing.id]: raw }));

    const parsed = Number(raw);
    if (raw.trim() !== "" && Number.isFinite(parsed)) {
      setCustomAmounts((prev) => ({ ...prev, [pricing.id]: parsed }));
      upsertSelectedAmount(pricing, parsed);
    }
  };

  const handleAmountInputBlur = (pricing: Ticket) => {
    setAmountInputs((prev) => {
      const next = { ...prev };
      delete next[pricing.id];
      return next;
    });

    if (getCustomAmount(pricing) < getMinimumAmount(pricing)) {
      updateCustomAmount(pricing, getMinimumAmount(pricing));
    }
  };

  const addToSelected = (sP: Ticket) => {
    const amount = isVariablePricing(sP) ? getCustomAmount(sP) : undefined;

    const minimumAmount = getMinimumAmount(sP);

    if (isVariablePricing(sP) && (amount ?? 0) < minimumAmount) {
      setCerror(
        `Support amount must be at least ${minimumAmount.toLocaleString()} ${currency}.`,
      );
      return;
    }

    setCerror(null);

    setSelected((prevSelected) => {
      const existingTicket = prevSelected.find(
        (ticket) => ticket.pricing.id === sP.id,
      );

      if (existingTicket) {
        if (isVariablePricing(sP)) {
          return prevSelected.map((ticket) =>
            ticket.pricing.id === sP.id
              ? { ...ticket, ticket_number: 1, amount }
              : ticket,
          );
        }

        return prevSelected.map((ticket) =>
          ticket.pricing.id === sP.id
            ? { ...ticket, ticket_number: ticket.ticket_number + 1 }
            : ticket,
        );
      }

      return [...prevSelected, { pricing: sP, ticket_number: 1, amount }];
    });
  };

  const removeFromSelected = (pricingIdToRemove: string) => {
    setSelected((prevSelected) =>
      prevSelected.flatMap((ticket) => {
        if (ticket.pricing.id !== pricingIdToRemove) return [ticket];
        if (ticket.ticket_number <= 1) return [];
        return [{ ...ticket, ticket_number: ticket.ticket_number - 1 }];
      }),
    );
  };

  const getTotal = () =>
    selected.reduce(
      (total, item) => total + item.ticket_number * getSelectedAmount(item),
      0,
    );

  useEffect(() => {
    setCerror(null);
  }, [steps]);

  useEffect(() => {
    setSteps(1);
    setSelected([]);
    setCustomAmounts({});
    setAmountInputs({});
  }, [data?.id]);

  const handlePayment = (data: any) => {
    const {
      personalPhoneCountryCode,
      phoneCountryCode,
      personalPhoneNumber,
      phoneNumber,
      ...rest
    } = data;
    const refined = {
      ...rest,
      personalPhoneNumber: formatInternationalPhone(
        personalPhoneCountryCode,
        personalPhoneNumber,
      ),
      phoneNumber: formatInternationalPhone(phoneCountryCode, phoneNumber),
      pricingIds: selected.map((it) => {
        const item = {
          pricingId: it.pricing.id,
          ticket_number: it.ticket_number,
        };
        return isVariablePricing(it.pricing)
          ? { ...item, amount: getSelectedAmount(it) }
          : item;
      }),
    } as PaymentPayload;
    payment({ status: !!user, data: refined });
  };

  useEffect(() => {
    if (payOptions.isSuccess) {
      setSteps(1);
      setSelected([]);
      reset();
    }
  }, [payOptions.isSuccess, reset]);

  const visiblePricings = sortTickets(data?.pricings ?? []);
  const posterImage = data?.image || campaign.poster;
  const currency = formatCampaignCurrency(data?.currency);
  const eventDate = data ? new Date(data.date) : null;

  return (
    <AnimatedContainer>
      <div className="min-h-screen bg-[#14261C] text-white">
        {isLoading ? (
          <div className="flex min-h-[500px] w-full items-center justify-center text-center text-white/60">
            One moment please...
          </div>
        ) : null}

        {!isCampaignSlug && !isLoading ? (
          <div className="flex min-h-[500px] w-full items-center justify-center text-center text-white/60">
            Event not found.
          </div>
        ) : null}

        {data && (
          <AnimatedContainer>
            <section className="relative overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url('${posterImage}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#14261C] via-[#1F3D2C]/90 to-[#E06A1F]/20" />
              <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#14261C] to-transparent" />

              <div className="relative mx-auto grid w-full max-w-[1500px] grid-cols-1 items-end gap-8 px-5 py-12 md:px-12 lg:grid-cols-[1fr_360px] lg:px-16">
                <div className="max-w-3xl">
                  <img
                    src={campaign.logo}
                    alt={campaign.fullTitle}
                    className="mb-6 w-48 drop-shadow-[0_18px_50px_rgba(224,106,31,0.35)]"
                  />
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#F0A93E]">
                    Hosted by {campaign.host}
                  </p>
                  <h1 className="mt-4 text-4xl font-black uppercase leading-[1.02] text-white sm:text-5xl">
                    {data.title}
                  </h1>
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    <EventFact
                      icon={<FiGift />}
                      label="Access"
                      value="Entrance is Free"
                    />
                    <EventFact
                      icon={<FiCalendar />}
                      label="Date"
                      value={eventDate ? formatShortDate(eventDate) : "Soon"}
                    />
                    <EventFact
                      icon={<FiClock />}
                      label="Doors Open"
                      value={campaign.lineup}
                    />
                    <EventFact
                      icon={<FiMic />}
                      label="Host"
                      value={campaign.host}
                    />
                    <EventFact
                      icon={<FiMapPin />}
                      label="Venue"
                      value={data.location}
                    />
                  </div>
                </div>

                <div className="hidden overflow-hidden rounded-lg border border-white/15 bg-white/10 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.35)] lg:block">
                  <div
                    className="aspect-square rounded-md bg-cover bg-center"
                    style={{ backgroundImage: `url('${posterImage}')` }}
                  />
                </div>
              </div>
            </section>

            <main className="bg-[#FBF7EE] text-[#14261C]">
              <div className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-8 px-5 py-10 md:px-12 lg:grid-cols-[1fr_380px] lg:px-16">
                <section>
                  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#E06A1F]">
                        Invitation
                      </p>
                      <h2 className="mt-2 text-2xl font-black uppercase text-[#14261C] sm:text-3xl">
                        Get your sponsorship invitation.
                      </h2>
                    </div>

                    <div className="flex items-center gap-2 text-sm font-bold text-[#14261C]/65">
                      <FiRefreshCw />
                      Sponsorship invitations are available with limited
                      reserved seats
                    </div>
                  </div>

                  <div className="mt-3">
                    <p className="text-sm font-bold text-[#14261C]/75">
                      Entrance to the event is free. You can sponsor your
                      invitation below.
                    </p>
                    <p className="mt-1 text-xs font-semibold text-[#14261C]/55">
                      Choose any amount of your choice from{" "}
                      {minimumSupportAmount.toLocaleString()} {currency}.
                    </p>
                  </div>

                  {cerror && <Alert tone="error">{cerror}</Alert>}
                  {payOptions.isSuccess && (
                    <Alert tone="success">Payment initiated.</Alert>
                  )}
                  {payOptions.isError && (
                    <Alert tone="error">
                      {payOptions.error
                        ? (payOptions.error as any).data?.message
                        : "Something went wrong, payment failed"}
                    </Alert>
                  )}

                  {steps === 1 && (
                    <>
                      <div className="mt-6 grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                        {visiblePricings.length === 0 ? (
                          <div className="col-span-2 flex min-h-32 items-center justify-center rounded-lg border border-[#F0D2AE] bg-white text-center text-[#14261C]/60">
                            Invitations will appear when the next edition is
                            published.
                          </div>
                        ) : null}
                        {visiblePricings.map((item) => (
                          <div
                            key={item.id}
                            className={`flex flex-wrap items-center justify-between gap-5 rounded-lg border border-[#F0D2AE] bg-white p-4 shadow-[0_18px_50px_rgba(3,25,34,0.08)] ${
                              isVariablePricing(item) ? "sm:col-span-2" : ""
                            }`}
                          >
                            <div className="flex flex-row items-center gap-4">
                              <div className="rounded-md bg-[#FCE7D2] p-3 text-2xl text-[#E06A1F]">
                                <BsTicketPerforated />
                              </div>
                              <div className="flex flex-col gap-1 font-semibold capitalize">
                                <p className="text-lg font-black uppercase">
                                  {displayTicketType(item.type)}
                                </p>
                                {isVariablePricing(item) ? (
                                  <p className="text-sm text-[#14261C]/65">
                                    Become a sponsor. Sponsorship contribution
                                    begins at{" "}
                                    <b>
                                      {getMinimumAmount(item).toLocaleString()}{" "}
                                      {currency}
                                    </b>
                                    .
                                  </p>
                                ) : (
                                  <p>
                                    <b>{item.price.toLocaleString()}</b>{" "}
                                    {currency}
                                  </p>
                                )}
                              </div>
                            </div>
                            {isVariablePricing(item) ? (
                              <div className="flex w-full flex-col gap-4">
                                <div>
                                  <p className="text-xs font-black uppercase tracking-[0.12em] text-[#14261C]/55">
                                    Quick suggestions
                                  </p>
                                  <p className="mt-1 text-sm text-[#14261C]/60">
                                    Pick one or type your own amount below.
                                  </p>
                                </div>
                                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                                  {supportAmountOptions.map((amount) => {
                                    const isSelected =
                                      getCustomAmount(item) === amount;

                                    return (
                                      <button
                                        key={amount}
                                        type="button"
                                        onClick={() =>
                                          updateCustomAmount(item, amount)
                                        }
                                        className={`rounded-md border px-2 py-2 text-xs font-black transition ${
                                          isSelected
                                            ? "border-[#E06A1F] bg-[#E06A1F] text-white"
                                            : "border-[#F0D2AE] bg-[#FBF7EE] text-[#14261C] hover:border-[#E06A1F]"
                                        }`}
                                      >
                                        {amount.toLocaleString()}
                                      </button>
                                    );
                                  })}
                                </div>
                                <label
                                  htmlFor={`amount-${item.id}`}
                                  className="text-xs font-black uppercase tracking-[0.12em] text-[#14261C]/55"
                                >
                                  Your sponsorship amount
                                </label>
                                <div
                                  className={`flex overflow-hidden rounded-md border bg-[#FBF7EE] focus-within:border-[#E06A1F] ${
                                    isCustomAmountValid(item)
                                      ? "border-[#F0D2AE]"
                                      : "border-red-500"
                                  }`}
                                >
                                  <input
                                    id={`amount-${item.id}`}
                                    type="number"
                                    min={getMinimumAmount(item)}
                                    step="1000"
                                    value={getAmountInputValue(item)}
                                    onChange={(event) =>
                                      handleAmountInputChange(
                                        item,
                                        event.target.value,
                                      )
                                    }
                                    onBlur={() => handleAmountInputBlur(item)}
                                    className="min-w-0 flex-1 bg-transparent px-3 py-2 focus:outline-none"
                                  />
                                  <span className="border-l border-[#F0D2AE] px-3 py-2 text-sm font-bold text-[#14261C]/55">
                                    {currency}
                                  </span>
                                </div>
                                {!isCustomAmountValid(item) && (
                                  <p className="text-sm font-bold text-red-600">
                                    Minimum sponsorship amount is{" "}
                                    {getMinimumAmount(item).toLocaleString()}{" "}
                                    {currency}.
                                  </p>
                                )}
                              </div>
                            ) : null}
                            {!isVariablePricing(item) ? (
                              <div className="flex flex-row items-center text-[#14261C]">
                                <button
                                  type="button"
                                  onClick={() => removeFromSelected(item.id)}
                                  className="rounded-md p-2 text-[1.5rem] transition hover:bg-[#FCE7D2]"
                                  aria-label={`Remove ${displayTicketType(item.type)} invitation`}
                                >
                                  <CgChevronLeftO />
                                </button>
                                <div className="mx-2 min-w-11 rounded-md bg-[#FCE7D2] px-3 py-2 text-center text-[1rem] font-black text-[#E06A1F]">
                                  {getTicketNumber(item.id)}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addToSelected(item)}
                                  className="rounded-md p-2 text-[1.5rem] transition hover:bg-[#FCE7D2]"
                                  aria-label={`Add ${displayTicketType(item.type)} invitation`}
                                >
                                  <CgChevronRightO />
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          selected.length === 0
                            ? setCerror(
                                "Please select an invitation and enter a sponsorship amount.",
                              )
                            : selected.some(
                                  (item) =>
                                    isVariablePricing(item.pricing) &&
                                    getSelectedAmount(item) <
                                      getMinimumAmount(item.pricing),
                                )
                              ? setCerror(
                                  `Support amount must be at least ${
                                    selected.find((item) =>
                                      isVariablePricing(item.pricing),
                                    )
                                      ? getMinimumAmount(
                                          selected.find((item) =>
                                            isVariablePricing(item.pricing),
                                          )!.pricing,
                                        ).toLocaleString()
                                      : minimumSupportAmount.toLocaleString()
                                  } ${currency}.`,
                                )
                              : setSteps(2)
                        }
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#E06A1F] py-3 text-sm font-black uppercase tracking-[0.1em] text-white transition hover:bg-[#F08A3C] sm:w-56"
                      >
                        Continue <GoArrowRight className="text-2xl" />
                      </button>
                    </>
                  )}

                  {steps === 2 && (
                    <>
                      <div className="mt-6 flex flex-col gap-3">
                        <div className="flex flex-row items-center gap-2 py-3 text-sm font-black uppercase tracking-[0.1em]">
                          <IoArrowBackCircleOutline
                            onClick={() => setSteps(1)}
                            className="cursor-pointer text-2xl"
                          />{" "}
                          Summary
                        </div>
                        <div className="rounded-lg border border-[#F0D2AE] bg-white p-4">
                          {selected.map((sel) => (
                            <div
                              key={sel.pricing.id}
                              className="mb-3 flex flex-row justify-between border-b border-[#F0D2AE] pb-3 last:mb-0 last:border-b-0 last:pb-0"
                            >
                              <div className="flex flex-col font-black uppercase">
                                {displayTicketType(sel.pricing.type)}
                                <p className="text-sm font-bold text-[#E06A1F]">
                                  {isVariablePricing(sel.pricing)
                                    ? "Sponsorship contribution"
                                    : `${sel.ticket_number} Ticket${
                                        sel.ticket_number > 1 ? "s" : ""
                                      }`}
                                </p>
                              </div>
                              <p className="font-black">
                                {(
                                  getSelectedAmount(sel) * sel.ticket_number
                                ).toLocaleString()}{" "}
                                {currency}
                              </p>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-row justify-between rounded-lg bg-[#14261C] p-4 font-black uppercase text-white">
                          <h3>Total amount</h3>
                          <p>
                            {getTotal().toLocaleString()} {currency}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSteps(3)}
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#E06A1F] py-3 text-sm font-black uppercase tracking-[0.1em] text-white transition hover:bg-[#F08A3C] sm:w-56"
                      >
                        Checkout <GoArrowRight className="text-2xl" />
                      </button>
                    </>
                  )}

                  {steps === 3 && (
                    <form
                      className="mt-6 flex w-full flex-col items-start"
                      onSubmit={handleSubmit(handlePayment)}
                    >
                      <div className="w-full">
                        <div className="flex flex-row items-center gap-2 py-3 text-sm font-black uppercase tracking-[0.1em]">
                          <IoArrowBackCircleOutline
                            onClick={() => setSteps(2)}
                            className="cursor-pointer text-2xl"
                          />{" "}
                          Checkout
                        </div>
                        <div className="grid w-full grid-cols-1 gap-4 rounded-lg border border-[#F0D2AE] bg-white p-4 sm:grid-cols-2">
                          <div className="col-span-2 flex flex-col gap-2">
                            <label htmlFor="email" className="font-bold">
                              Email
                            </label>
                            <input
                              type="email"
                              {...register("email", {
                                required: "Email is required",
                              })}
                              className="w-full rounded-md border border-[#F0D2AE] bg-[#FBF7EE] px-3 py-3 focus:outline-none"
                              placeholder="Enter your email address"
                            />
                            {errors.email && (
                              <p className="text-sm text-red-500">
                                {errors.email.message}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                            <label htmlFor="name" className="font-bold">
                              Names
                            </label>
                            <input
                              type="text"
                              {...register("name", {
                                required: "Name is required",
                              })}
                              className="w-full rounded-md border border-[#F0D2AE] bg-[#FBF7EE] px-3 py-3 focus:outline-none"
                              placeholder="Enter your full names"
                            />
                            {errors.name && (
                              <p className="text-sm text-red-500">
                                {errors.name.message}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                            <label
                              htmlFor="personalPhoneNumber"
                              className="font-bold"
                            >
                              Phone number
                            </label>
                            <PhoneInput
                              codeRegistration={register(
                                "personalPhoneCountryCode",
                              )}
                              phoneRegistration={register(
                                "personalPhoneNumber",
                                {
                                  required: "Phone number is required",
                                  validate: (value) =>
                                    isValidPhoneNumber(
                                      value,
                                      watch("personalPhoneCountryCode"),
                                    ) || "Phone number invalid",
                                },
                              )}
                              placeholder={
                                countryCodes.find(
                                  (country) =>
                                    country.code ===
                                    watch("personalPhoneCountryCode"),
                                )?.localExample ?? "7XXXXXXXX"
                              }
                            />
                            {errors.personalPhoneNumber && (
                              <p className="text-sm text-red-500">
                                {errors.personalPhoneNumber.message}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2 hidden border-y py-3">
                            <div className="flex flex-col gap-2">
                              <label htmlFor="promoCode" className="font-bold">
                                Promo Code{" "}
                                <span className="text-xs text-primary">
                                  (optional)
                                </span>
                              </label>
                              <div className="flex flex-row gap-1">
                                <input
                                  type="text"
                                  {...register("promoCode")}
                                  className="w-full rounded-md border border-[#F0D2AE] bg-[#FBF7EE] px-3 py-3 focus:outline-none"
                                  placeholder="Enter a promo code"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    watch("promoCode")
                                      ? verify({
                                          promo: watch("promoCode") ?? "",
                                        })
                                      : setError("promoCode", {
                                          message: "Promo code is required",
                                        })
                                  }
                                  className="rounded-md bg-primary p-2 text-white"
                                >
                                  {promoOptions.isLoading
                                    ? "Loading"
                                    : "Verify"}
                                </button>
                              </div>
                              {errors.promoCode && (
                                <p className="text-sm text-red-500">
                                  {errors.promoCode.message}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="col-span-2 flex items-center justify-center">
                            <img
                              src={mtn_logo}
                              className="w-20"
                              alt="mtn-logo"
                            />
                          </div>
                          <div className="col-span-2 flex flex-col gap-2">
                            <label htmlFor="phoneNumber" className="font-bold">
                              Momo Phone number
                            </label>
                            <PhoneInput
                              codeRegistration={register("phoneCountryCode")}
                              phoneRegistration={register("phoneNumber", {
                                required: "Momo phone number is required",
                                validate: (value) =>
                                  isValidPhoneNumber(
                                    value,
                                    watch("phoneCountryCode"),
                                  ) || "Phone number invalid",
                              })}
                              placeholder={
                                countryCodes.find(
                                  (country) =>
                                    country.code === watch("phoneCountryCode"),
                                )?.localExample ?? "7XXXXXXXX"
                              }
                            />
                            {errors.phoneNumber && (
                              <p className="text-sm text-red-500">
                                {errors.phoneNumber.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        disabled={payOptions.isLoading}
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#E06A1F] py-3 text-sm font-black uppercase tracking-[0.1em] text-white transition hover:bg-[#F08A3C] disabled:bg-gray-300 sm:w-56"
                      >
                        {payOptions.isLoading ? "Loading..." : "Pay Now"}
                      </button>
                    </form>
                  )}
                </section>

                <aside className="h-fit rounded-lg bg-[#14261C] p-5 text-white shadow-[0_24px_70px_rgba(3,25,34,0.18)] lg:sticky lg:top-28">
                  <div
                    className="aspect-square rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url('${posterImage}')` }}
                  />
                  <div className="mt-5">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#F0A93E]">
                      Current edition
                    </p>
                    <h3 className="mt-2 text-2xl font-black text-white">
                      {data.title}
                    </h3>
                    <p className="mt-2 flex items-center gap-2 text-white/65">
                      <FiCalendar />{" "}
                      {eventDate ? formatLongDate(eventDate) : "Coming soon"}
                    </p>
                    <p className="mt-2 flex items-center gap-2 text-white/65">
                      <FiMapPin /> {data.location}, {data.country}
                    </p>
                    <p className="mt-2 flex items-center gap-2 text-white/65">
                      <FiMic /> Hosted by {campaign.host}
                    </p>
                  </div>
                  <div className="mt-5 border-t border-white/10 pt-5">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#F0A93E]">
                      Prices
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      {visiblePricings.map((item) => (
                        <div
                          key={item.id}
                          className="flex justify-between border-b border-white/10 pb-2 text-sm last:border-b-0"
                        >
                          <span className="font-black uppercase">
                            {displayTicketType(item.type)}
                          </span>
                          <span>
                            {isVariablePricing(item)
                              ? `From ${getMinimumAmount(item).toLocaleString()}`
                              : item.price.toLocaleString()}{" "}
                            {currency}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </main>
          </AnimatedContainer>
        )}
      </div>
    </AnimatedContainer>
  );
}

function EventFact({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#E06A1F] text-xl text-white">
          {icon}
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.12em] text-white/45">
            {label}
          </p>
          <p className="text-base font-black text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function Alert({
  children,
  tone,
}: {
  children: ReactNode;
  tone: "error" | "success";
}) {
  const isError = tone === "error";

  return (
    <motion.div
      initial={{ y: "-10px", opacity: 0.5 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`mt-4 flex w-full flex-row items-center gap-2 border-l-4 px-3 py-2 text-sm ${
        isError
          ? "border-red-500 bg-red-50 text-red-700"
          : "border-green-500 bg-green-50 text-green-700"
      }`}
    >
      <HiOutlineExclamationCircle />
      {children}
    </motion.div>
  );
}

function PhoneInput({
  codeRegistration,
  phoneRegistration,
  placeholder,
}: {
  codeRegistration: any;
  phoneRegistration: any;
  placeholder: string;
}) {
  return (
    <div className="flex w-full overflow-hidden rounded-md border border-[#F0D2AE] bg-[#FBF7EE] focus-within:border-[#E06A1F]">
      <select
        {...codeRegistration}
        className="min-w-[132px] border-r border-[#F0D2AE] bg-[#FCE7D2] px-3 py-3 text-sm font-black text-[#14261C] focus:outline-none"
        aria-label="Country code"
      >
        {countryCodes.map((country) => (
          <option key={country.code} value={country.code}>
            {country.flag} +{country.code}
          </option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="numeric"
        {...phoneRegistration}
        className="min-w-0 flex-1 bg-transparent px-3 py-3 focus:outline-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function isVariablePricing(pricing: Ticket) {
  return (
    pricing.fixed_amount === false ||
    normalizeTicketType(pricing.type) === "DONATION"
  );
}

function getLocalPhoneNumber(phoneNumber?: string) {
  if (!phoneNumber) return "";
  const normalized = phoneNumber.replace(/\D/g, "");
  const country = countryCodes.find((item) => normalized.startsWith(item.code));

  return country ? normalized.slice(country.code.length) : normalized;
}

function formatInternationalPhone(
  countryCode: string,
  localPhoneNumber: string,
) {
  return `${countryCode}${localPhoneNumber.replace(/\D/g, "").replace(/^0+/, "")}`;
}

function isValidPhoneNumber(localPhoneNumber: string, countryCode?: string) {
  const normalized = localPhoneNumber.replace(/\D/g, "").replace(/^0+/, "");

  if (countryCode === "250") {
    return /^7[2389]\d{7}$/.test(normalized);
  }

  return /^\d{7,12}$/.test(normalized);
}

function formatShortDate(date: Date) {
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
  });
}

function formatLongDate(date: Date) {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
